import React, { useContext, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { HighCostContext } from "../../../context/HighCostContext";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useTranslation } from "react-i18next";
import { formatMoneyCell } from "../../../common/helpers";
import useClinicalLocations from "../../../hooks/useClinicalLocations";
import { useObservationsModal } from "../../../../../context/ObservationsModalContext/ObservationsModalContext";
import { getObservationsColumn } from "../../../../../components/Advanced/DataGrid/observationsColumn";

function HighCostTable({ selectedLocation, setSelectedLocation }) {
  const { t } = useTranslation();
  const { showObservationsModal } = useObservationsModal();
  const { locations: clinicalLocations } = useClinicalLocations();
  const { highCostPatients, loading, error, setLocationId } = useContext(HighCostContext);
  const navigate = useNavigate();
  const location = useLocation();
  const tableRef = useRef(null);

  // Scroll to top of table whenever location filter changes
  useEffect(() => {
    if (tableRef.current) {
      setTimeout(() => {
        tableRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    }
  }, [selectedLocation]);

  const handleLocationChange = (locId) => {
    setSelectedLocation(locId);
    setLocationId(locId === "" ? null : locId);
  };

  const handleViewPatient = (cipId, row) => {
    navigate(`/patient-info/${cipId}`, {
      state: { patientRow: row, returnPath: location.pathname },
    });
  };

  const columns = [
    getObservationsColumn(showObservationsModal, 5, {
      getCipId: (row) => row.varCipId,
      getPatientName: (row) => row.memberFullName || "",
    }),
    {
      field: "actions",
      headerName: "",
      width: 60,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Tooltip title="View Patient Info">
          <IconButton
            size="small"
            color="primary"
            onClick={(e) => {
              e.stopPropagation();
              handleViewPatient(params.row.varCipId, params.row);
            }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
    { field: "healthStatus", headerName: t("HighCost.HighCostInformation.HealthStatus"), width: 130 },
    { field: "memberId", headerName: t("HighCost.HighCostInformation.MemberID"), width: 130 },
    { field: "memberFullName", headerName: t("HighCost.HighCostInformation.FullName"), width: 200 },
    {
      field: "totalCost",
      headerName: t("HighCost.HighCostInformation.TotalCost"),
      width: 130,
      valueGetter: (params) => formatMoneyCell(params),
    },
    { field: "newHighCost", headerName: t("HighCost.HighCostInformation.New"), width: 100 },
    { field: "memberEmail", headerName: t("HighCost.HighCostInformation.Email"), width: 200 },
    {
      field: "inpatientCost",
      headerName: t("HighCost.HighCostInformation.Inpatient"),
      width: 130,
      valueGetter: (params) => formatMoneyCell(params),
    },
    {
      field: "outpatientCost",
      headerName: t("HighCost.HighCostInformation.Outpatient"),
      width: 130,
      valueGetter: (params) => formatMoneyCell(params),
    },
    {
      field: "professionalCost",
      headerName: t("HighCost.HighCostInformation.Professional"),
      width: 130,
      valueGetter: (params) => formatMoneyCell(params),
    },
    {
      field: "pharmacyCost",
      headerName: t("HighCost.HighCostInformation.Pharmacy"),
      width: 130,
      valueGetter: (params) => formatMoneyCell(params),
    },
    {
      field: "virtualVisitCost",
      headerName: t("HighCost.HighCostInformation.VirtualVisit"),
      width: 130,
      valueGetter: (params) => formatMoneyCell(params),
    },
    {
      field: "ancillaryVisitCost",
      headerName: t("HighCost.HighCostInformation.Ancillary"),
      width: 130,
      valueGetter: (params) => formatMoneyCell(params),
    },
    { field: "medicalCenter", headerName: t("HighCost.HighCostInformation.MedicalCenter"), width: 150 },
    { field: "locationAddress", headerName: t("HighCost.HighCostInformation.LocationAddress"), width: 300 },
    { field: "primaryCareProvider", headerName: t("HighCost.HighCostInformation.PrimaryCareProvider"), width: 220 },
  ];

  if (loading) return <Typography>{t("Main.Loading")}</Typography>;
  if (error) return <Typography color="error">{t("HighCost.Error")}</Typography>;

  return (
    <>
      {/* Location Filter */}
      <Box sx={{ display: "flex", mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel>{t("Main.Admitted.FilterbyInsuranceLocation")}</InputLabel>
          <Select
            label={t("Main.Admitted.FilterbyInsuranceLocation")}
            value={selectedLocation ?? ""}
            onChange={(e) => handleLocationChange(e.target.value)}
          >
            <MenuItem value="">
              <em>{t("Main.Admitted.FilterbyInsuranceLocation")}</em>
            </MenuItem>
            {clinicalLocations.map((loc) => (
              <MenuItem key={loc.id} value={loc.id}>
                {loc.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Table */}
      <div ref={tableRef} style={{ height: 500, width: "100%" }}>
        <DataGrid
          getRowId={(row) => row.highCostId}
          rows={highCostPatients}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          disableSelectionOnClick
        />
      </div>
    </>
  );
}

export default HighCostTable;
